import styled from '@emotion/styled';
import classNames from 'classnames';
import throttle from 'lodash-es/throttle';
import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { semantic_colors } from '../../foundation';
import { IconPlus, IconProps } from '../icons/generated';
import { Stack } from '../stack';

const SCROLL_FADE_WIDTH = 16;
const TAB_GAP = 2;

export interface BoxTabsContextProps {
  activeTabId?: string;
  onChange?: (id: string) => void;
}

export const BoxTabContext = createContext<BoxTabsContextProps | null>(null);

export const useBoxTabContext = () => {
  const context = useContext(BoxTabContext);

  if (!context) {
    throw new Error('BoxTabContext is not defined');
  }
  return context;
};

type BaseBoxTabsProps = {
  className?: string;
  /**
   * 활성화된 탭 ID
   */
  activeTabId?: string;
  /**
   *
   * 탭 변경 이벤트
   * 변경된 탭 ID
   */
  onChange: (id: string) => void;
};

type AddActionProps =
  | {
      /**
       * 추가 버튼 클릭 이벤트
       */
      onAdd: () => void;
      renderAddAction?: never;
    }
  | {
      onAdd?: never;
      /**
       * 커스텀 액션 렌더링 함수
       */
      renderAddAction: (action: {
        iconComponent: React.ComponentType<IconProps>;
        iconProps: IconProps;
      }) => React.ReactNode;
    }
  | {
      onAdd?: never;
      renderAddAction?: never;
    };

export type BoxTabsProps = PropsWithChildren<BaseBoxTabsProps & AddActionProps>;

export const BoxTabs = ({ className, children, activeTabId, onChange, onAdd, renderAddAction }: BoxTabsProps) => {
  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);
  const initialRenderRef = useRef(true);

  const [fade, setFade] = useState({ left: false, right: false });

  // onAdd 혹은 renderAddAction이 있는 경우 버튼이 노출 됩니다.
  const showAddButton = typeof onAdd === 'function' || typeof renderAddAction === 'function';

  const updateMask = useCallback(() => {
    const scrollContainer = scrollWrapperRef.current;
    if (!scrollContainer) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
    const hasOverflow = scrollWidth > clientWidth;

    setFade({
      left: hasOverflow && scrollLeft > 0,
      right: hasOverflow && scrollLeft + clientWidth < scrollWidth - 1,
    });
  }, []);

  const handleTabScroll = throttle(() => {
    updateMask();
  }, 300);

  const updateActiveScrollPosition = useCallback(
    (initial: boolean) => {
      const scrollContainer = scrollWrapperRef.current;

      if (scrollContainer && activeTabId) {
        const activeTabElement = scrollContainer.querySelector(`#${activeTabId}`) as HTMLElement;

        if (activeTabElement) {
          const scrollContainerRect = scrollContainer.getBoundingClientRect();
          const currentTabItemRect = activeTabElement.getBoundingClientRect();

          const correction =
            scrollContainer.scrollLeft - scrollContainer.clientWidth / 2 + currentTabItemRect.width / 2;

          const PADDING_OFFSET = 16;
          const startLeft = initial ? currentTabItemRect.left + PADDING_OFFSET : currentTabItemRect.left;

          scrollContainer.scrollTo({
            left: startLeft - scrollContainerRect.left + correction,
            behavior: initial ? 'auto' : 'smooth',
          });
        }
      }
    },
    [activeTabId],
  );

  useEffect(() => {
    updateActiveScrollPosition(initialRenderRef.current);
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
    }
    updateMask();
  }, [updateActiveScrollPosition, updateMask]);

  useEffect(() => {
    updateActiveScrollPosition(false);
  }, [activeTabId, updateActiveScrollPosition]);

  useEffect(() => {
    const scrollContainer = scrollWrapperRef.current;
    if (!scrollContainer || typeof window === 'undefined' || !('ResizeObserver' in window)) return;

    const resizeObserver = new window.ResizeObserver(() => {
      updateMask();
    });
    resizeObserver.observe(scrollContainer);

    return () => resizeObserver.disconnect();
  }, [updateMask]);

  return (
    <BoxTabContext.Provider value={{ activeTabId, onChange }}>
      <TabContainer className={classNames('pds-box-tabs', className)}>
        <ScrollWrapper
          ref={scrollWrapperRef}
          $fadeLeft={fade.left}
          $fadeRight={fade.right}
          role='tablist'
          onScroll={handleTabScroll}
        >
          <Stack gap={TAB_GAP}>{children}</Stack>
        </ScrollWrapper>
        {showAddButton && (
          <AddButton onClick={onAdd} aria-label='Add tab'>
            {typeof renderAddAction === 'function' ? (
              renderAddAction({
                iconComponent: IconPlus,
                iconProps: { size: 16, color: semantic_colors.content.primary },
              })
            ) : (
              <IconPlus size={16} color={semantic_colors.content.primary} />
            )}
          </AddButton>
        )}
      </TabContainer>
    </BoxTabContext.Provider>
  );
};

const TabContainer = styled.nav`
  position: relative;
  width: 100%;
  display: flex;
  align-items: flex-end;
  gap: ${TAB_GAP}px;
  border-bottom: 1px solid ${semantic_colors.border.primary};
`;

const ScrollWrapper = styled.div<{
  $fadeLeft: boolean;
  $fadeRight: boolean;
}>`
  display: flex;
  align-items: flex-end;
  margin-bottom: -1px;
  z-index: 2;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  background-color: transparent;
  flex: 0 1 auto;
  min-width: 0;
  ${({ $fadeLeft, $fadeRight }) => {
    if (!$fadeLeft && !$fadeRight) return '';

    const start = $fadeLeft ? `transparent 0, #000 ${SCROLL_FADE_WIDTH}px` : '#000 0';
    const end = $fadeRight ? `#000 calc(100% - ${SCROLL_FADE_WIDTH}px), transparent 100%` : '#000 100%';
    const gradient = `linear-gradient(to right, ${start}, ${end})`;

    return `
      -webkit-mask-image: ${gradient};
      mask-image: ${gradient};
    `;
  }}

  ::-webkit-scrollbar {
    display: none;
  }
`;

const AddButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 36px;
  flex-shrink: 0;

  border: 1px solid ${semantic_colors.border.primary};
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  background-color: ${semantic_colors.background.surface};
  cursor: pointer;
  z-index: 10;
  transition: background-color 0.1s linear;

  &:hover {
    background-color: ${semantic_colors.fill.tertiary};
  }

  &:active {
    background-color: ${semantic_colors.fill.secondary};
  }

  &:disabled {
    cursor: not-allowed;
    background-color: ${semantic_colors.background.disabled};
  }
`;
